import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiTrendingUp, FiTarget, FiBookOpen, FiShield, FiAward } = FiIcons;

const About = () => {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white rounded-xl p-8 mb-12"
      >
        <div className="text-center">
          <SafeIcon icon={FiUser} className="text-4xl mb-4 mx-auto" />
          <h1 className="text-3xl md:text-4xl font-bold mb-4">About iNVESTT Goal vs Returns Simulator</h1>
          <p className="text-xl text-indigo-100">
            An educational tool to help Indian investors understand the critical relationship between goals, fund categories, and returns
          </p>
        </div>
      </motion.div>

      {/* Creator Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="bg-white rounded-xl shadow-lg p-8 mb-8"
      >
        <div className="flex items-center space-x-4 mb-6">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-3 rounded-full">
            <SafeIcon icon={FiAward} className="text-white text-2xl" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Created by Gopi Krishna</h2>
            <p className="text-indigo-600 font-semibold">SEBI Registered Research Analyst (INH000010858)</p>
            <p className="text-gray-600">Founder of iNVESTT</p>
          </div>
        </div>
        
        <p className="text-gray-700 leading-relaxed">
          This simulator is designed specifically for Indian mutual fund investors to understand why choosing the right fund category 
          for each goal is more important than just setting financial targets. As a SEBI registered research analyst, 
          I've seen countless investors delay their goals by years simply because they chose the wrong investment vehicles.
        </p>
      </motion.div>

      {/* What This Tool Does */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="bg-white rounded-xl shadow-lg p-8 mb-8"
      >
        <div className="flex items-center space-x-3 mb-6">
          <SafeIcon icon={FiTarget} className="text-2xl text-indigo-600" />
          <h2 className="text-2xl font-bold text-gray-900">What This Tool Does</h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Core Functions</h3>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start space-x-2">
                <span className="text-indigo-600 font-bold">•</span>
                <span>Shows how different fund categories impact your goals</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-indigo-600 font-bold">•</span>
                <span>Calculates the cost of choosing wrong categories</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-indigo-600 font-bold">•</span>
                <span>Demonstrates potential delays or early achievements</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-indigo-600 font-bold">•</span>
                <span>Provides category recommendations based on timeframes</span>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Insights</h3>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start space-x-2">
                <span className="text-green-600 font-bold">•</span>
                <span>Why returns matter more than "goal labels"</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-green-600 font-bold">•</span>
                <span>How to match timeframes with fund categories</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-green-600 font-bold">•</span>
                <span>Impact of inflation on different types of goals</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-green-600 font-bold">•</span>
                <span>Portfolio diversification across goal timeframes</span>
              </li>
            </ul>
          </div>
        </div>
      </motion.div>

      {/* Framework */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="bg-white rounded-xl shadow-lg p-8 mb-8"
      >
        <div className="flex items-center space-x-3 mb-6">
          <SafeIcon icon={FiTrendingUp} className="text-2xl text-indigo-600" />
          <h2 className="text-2xl font-bold text-gray-900">Our Return Framework</h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
              <h4 className="font-semibold text-red-900">0-2 Years (Short-term)</h4>
              <p className="text-red-700 text-sm">Target: 5-7% | Use: Liquid, Ultra-short debt funds</p>
            </div>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
              <h4 className="font-semibold text-yellow-900">1-3 Years (Near-term)</h4>
              <p className="text-yellow-700 text-sm">Target: 7-9% | Use: Ultra-short, Conservative hybrid</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
              <h4 className="font-semibold text-blue-900">3-7 Years (Medium-term)</h4>
              <p className="text-blue-700 text-sm">Target: 10-13% | Use: Balanced advantage, Aggressive hybrid</p>
            </div>
            
            <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
              <h4 className="font-semibold text-green-900">7+ Years (Long-term)</h4>
              <p className="text-green-700 text-sm">Target: 12-15% | Use: Large-cap, Flexi-cap, Multi-cap, Index</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Important Notes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="bg-white rounded-xl shadow-lg p-8"
      >
        <div className="flex items-center space-x-3 mb-6">
          <SafeIcon icon={FiShield} className="text-2xl text-indigo-600" />
          <h2 className="text-2xl font-bold text-gray-900">Important Disclaimers</h2>
        </div>

        <div className="space-y-4 text-gray-700">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-900 mb-2 flex items-center space-x-2">
              <SafeIcon icon={FiBookOpen} className="text-yellow-600" />
              <span>Educational Purpose Only</span>
            </h3>
            <p className="text-yellow-800 text-sm">
              This tool is designed for educational purposes to help you understand the relationship between goals, 
              timeframes, and fund categories. All calculations are based on assumptions and historical patterns.
            </p>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-900 mb-2 flex items-center space-x-2">
              <SafeIcon icon={FiShield} className="text-red-600" />
              <span>Not Financial Advice</span>
            </h3>
            <p className="text-red-800 text-sm">
              This is NOT personalized financial advice. Please consult a SEBI-registered investment advisor 
              before making any investment decisions. Past performance does not guarantee future results.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">Assumptions Used</h3>
            <ul className="text-blue-800 text-sm space-y-1">
              <li>• Return assumptions based on historical category averages</li>
              <li>• Inflation rates: 6.5% general, 10.5% education, 9.5% healthcare</li>
              <li>• SIP calculations assume consistent monthly investments</li>
              <li>• Success probabilities are qualitative estimates, not guarantees</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default About;